import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MapPin, 
  Eye, 
  EyeSlash, 
  SpinnerGap,
  Truck,
  User,
  UsersThree,
  CaretRight,
  Flask,
  Desktop,
} from '@phosphor-icons/react';
import axios from 'axios';
import { useAuthStore } from '@/store';
import { useBrandStore } from '@/store';
import { settingsAPI } from '@/services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const { logoUrl, companyName, loginLogoSize, setBrandSettings } = useBrandStore();
  
  // Tamaños de logo para login
  const logoSizeClasses = {
    small: 'h-14 sm:h-16 lg:h-20',
    medium: 'h-20 sm:h-24 lg:h-28',
    large: 'h-28 sm:h-32 lg:h-40',
  };
  
  const [account, setAccount] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loginType, setLoginType] = useState('auto'); // auto, admin, subuser
  const [brandLoading, setBrandLoading] = useState(true);
  
  // Version preference: 'beta' or 'classic'
  const [versionMode, setVersionMode] = useState(() => {
    return localStorage.getItem('4sat_version_mode') || 'beta';
  });

  // Save version preference
  useEffect(() => {
    localStorage.setItem('4sat_version_mode', versionMode);
  }, [versionMode]);

  // Load brand settings from database on mount (public endpoint)
  useEffect(() => {
    const loadBrandSettings = async () => {
      try {
        const response = await settingsAPI.getBrandPublic();
        if (response.data) {
          const data = response.data;
          setBrandSettings({
            logoUrl: data.logo_url || null,
            companyName: data.company_name || '4Sat',
            primaryColor: data.primary_color || '#3b82f6',
            secondaryColor: data.secondary_color || '#10b981',
            loginLogoSize: data.login_logo_size || 'medium',
          });
        }
      } catch (err) {
        console.log('Could not load brand settings:', err.message);
      } finally {
        setBrandLoading(false);
      }
    };
    loadBrandSettings();
  }, [setBrandSettings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let response;
      let userType = loginType;
      
      if (loginType === 'auto') {
        // Try admin login first, then sub-user
        try {
          response = await axios.post(`${BACKEND_URL}/api/auth/login`, { 
            account, 
            password 
          });
          userType = 'admin';
        } catch (adminErr) {
          // If admin login fails with "Invalid signature" or similar, try sub-user
          if (adminErr.response?.status === 401) {
            try {
              response = await axios.post(`${BACKEND_URL}/api/auth/login/user`, { 
                account, 
                password 
              });
              userType = 'subuser';
            } catch (subErr) {
              throw adminErr; // Throw original error if both fail
            }
          } else {
            throw adminErr;
          }
        }
      } else if (loginType === 'admin') {
        response = await axios.post(`${BACKEND_URL}/api/auth/login`, { 
          account, 
          password 
        });
      } else {
        response = await axios.post(`${BACKEND_URL}/api/auth/login/user`, { 
          account, 
          password 
        });
      }
      
      const { token, user, tracksolid_url } = response.data;
      
      localStorage.setItem('4sat_token', token);
      localStorage.setItem('4sat_user', JSON.stringify({ ...user, loginType: userType }));
      localStorage.setItem('4sat_version_mode', versionMode);
      
      // Store tracksolid URL for classic mode
      if (tracksolid_url) {
        localStorage.setItem('4sat_tracksolid_url', tracksolid_url);
      }
      
      login(token, { ...user, loginType: userType, versionMode });
      
      // Navigate based on version mode
      if (versionMode === 'classic') {
        navigate('/classic');
      } else {
        navigate('/');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.includes('frequency') || detail?.includes('rate')) {
        setError('Demasiados intentos. Espera un momento e intenta de nuevo.');
      } else if (detail?.includes('Invalid signature')) {
        setError('Credenciales incorrectas. Verifica tu usuario y contraseña.');
      } else {
        setError(detail || 'Error de autenticación. Verifica tus credenciales.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel - Background Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative items-center justify-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1759310347467-578dfd846229?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxsb2dpc3RpY3MlMjB3YXJlaG91c2UlMjBmdXR1cmlzdGljfGVufDB8fHx8MTc3Mzg1OTgyNnww&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-background/95 to-background/50" />
        <div className="relative z-10 px-12 text-center">
          {/* 4SAT Logo */}
          <div className="flex flex-col items-center justify-center mb-6">
            <img 
              src="/assets/4sat-logo.png" 
              alt="4SAT" 
              className="h-16 object-contain mb-2"
            />
            <p className="text-sm text-gray-500 font-medium tracking-wide">
              Fatiga, Somnolencia y Distracción
            </p>
          </div>
          <h1 className="text-4xl font-bold mb-4 tracking-tight">
            Plataforma de Rastreo GPS
          </h1>
          <p className="text-gray-500 text-lg max-w-md">
            Monitorea tu flota en tiempo real con tecnología de vanguardia. 
            Control total desde cualquier dispositivo.
          </p>
          <div className="mt-8 flex gap-6 justify-center text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <MapPin size={18} className="text-secondary" />
              <span>Rastreo en vivo</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin size={18} className="text-secondary" />
              <span>Alertas instantáneas</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin size={18} className="text-secondary" />
              <span>Reportes detallados</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gray-100">
        <div className="w-full max-w-md slide-in-up">
          {/* Logo */}
          <div className="text-center mb-6">
            <div className="flex flex-col items-center justify-center mb-4">
              {logoUrl ? (
                <img 
                  src={logoUrl} 
                  alt={companyName || '4SAT'} 
                  className={`${logoSizeClasses[loginLogoSize] || logoSizeClasses.medium} object-contain`}
                  data-testid="login-logo"
                />
              ) : (
                <>
                  <img 
                    src="/assets/4sat-logo.png" 
                    alt="4SAT" 
                    className="h-16 object-contain lg:hidden"
                  />
                  <div className="hidden lg:flex items-center justify-center gap-3">
                    <div className="p-3 rounded-xl bg-primary/20 border border-primary/30">
                      <MapPin size={40} weight="duotone" className="text-primary" />
                    </div>
                    <h2 className="text-4xl font-bold tracking-tight">{companyName || '4SAT'}</h2>
                  </div>
                </>
              )}
            </div>
            <p className="text-gray-500">
              Ingresa tus credenciales para continuar
            </p>
          </div>

          {/* Version Mode Toggle */}
          <div className="mb-6">
            <label className="block text-xs text-gray-500 mb-2 text-center">
              Selecciona la versión de la plataforma
            </label>
            <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
              <button
                type="button"
                onClick={() => setVersionMode('classic')}
                className={`flex-1 h-12 rounded-md text-sm font-medium transition-all flex flex-col items-center justify-center gap-0.5 ${
                  versionMode === 'classic' 
                    ? 'bg-primary text-primary-foreground shadow-lg' 
                    : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
                }`}
              >
                <Desktop size={18} />
                <span>Clásica</span>
              </button>
              <button
                type="button"
                onClick={() => setVersionMode('beta')}
                className={`flex-1 h-12 rounded-md text-sm font-medium transition-all flex flex-col items-center justify-center gap-0.5 ${
                  versionMode === 'beta' 
                    ? 'bg-secondary text-secondary-foreground shadow-lg' 
                    : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
                }`}
              >
                <Flask size={18} />
                <span>Beta</span>
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              {versionMode === 'classic' && 'Interfaz original de Tracksolid con tu marca'}
              {versionMode === 'beta' && 'Nueva interfaz con funciones adicionales (en desarrollo)'}
            </p>
          </div>

          {/* Login Type Selector */}
          <div className="mb-6">
            <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
              <button
                type="button"
                onClick={() => setLoginType('auto')}
                className={`flex-1 h-10 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  loginType === 'auto' 
                    ? 'bg-white text-gray-800 shadow' 
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                <CaretRight size={16} /> Auto
              </button>
              <button
                type="button"
                onClick={() => setLoginType('admin')}
                className={`flex-1 h-10 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  loginType === 'admin' 
                    ? 'bg-white text-gray-800 shadow' 
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                <UsersThree size={16} /> Admin
              </button>
              <button
                type="button"
                onClick={() => setLoginType('subuser')}
                className={`flex-1 h-10 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  loginType === 'subuser' 
                    ? 'bg-white text-gray-800 shadow' 
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                <User size={16} /> Usuario
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              {loginType === 'auto' && 'Se detectará automáticamente el tipo de cuenta'}
              {loginType === 'admin' && 'Cuenta principal de Tracksolidpro'}
              {loginType === 'subuser' && 'Usuario creado por un administrador'}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div 
                className="p-4 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive text-sm fade-in"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-800">
                {loginType === 'subuser' ? 'Email' : 'Cuenta / Email'}
              </label>
              <input
                type="text"
                value={account}
                onChange={(e) => setAccount(e.target.value)}
                placeholder={loginType === 'subuser' ? 'usuario@email.com' : 'tu@email.com'}
                required
                data-testid="login-account-input"
                className="w-full h-11 px-4 rounded-lg bg-white border border-gray-200 text-gray-800 placeholder:text-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-800">
                Contraseña
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="login-password-input"
                  className="w-full h-11 px-4 pr-11 rounded-lg bg-white border border-gray-200 text-gray-800 placeholder:text-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800 transition-colors"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className={`w-full h-11 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 ${
                versionMode === 'classic' 
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90' 
                  : 'bg-secondary text-secondary-foreground hover:bg-secondary/90'
              }`}
            >
              {loading ? (
                <>
                  <SpinnerGap size={20} className="spinner" />
                  Iniciando sesión...
                </>
              ) : (
                <>
                  Iniciar Sesión
                  {versionMode === 'beta' && <Flask size={16} />}
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-xs text-gray-500">
              Powered by Tracksolid Pro API
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
